import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { fileUrl } from "@/lib/api";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { CheckCircle2, XCircle, Trash2, Plus, Eye, ImageOff, RotateCcw, Gift } from "lucide-react";
import { toast } from "sonner";
import {
  Dialog, DialogClose, DialogContent, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import { isFirebaseConfigured } from "@/lib/firebase";
import { grantFirebaseGift, listFirebaseOrders, updateFirebaseOrderStatus } from "@/lib/firebaseOrders";
import { deleteFirebaseApp, listFirebaseApps } from "@/lib/firebaseApps";
import { PRODUCT_CATALOG, isAppAvailable } from "@/lib/catalog";
import { getPaymentMethodLabel } from "@/lib/payments";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/lib/auth";

const LOCAL_ORDERS_KEY = "weborder.localOrders";
const HIDDEN_CATALOG_APPS_KEY = "weborder.hiddenCatalogApps";
const FALLBACK_APPS = PRODUCT_CATALOG;

function getLocalOrders() {
  try {
    return JSON.parse(localStorage.getItem(LOCAL_ORDERS_KEY) || "[]");
  } catch {
    return [];
  }
}

function setLocalOrders(orders) {
  localStorage.setItem(LOCAL_ORDERS_KEY, JSON.stringify(orders));
}

function getHiddenCatalogAppIds() {
  try {
    return JSON.parse(localStorage.getItem(HIDDEN_CATALOG_APPS_KEY) || "[]");
  } catch {
    return [];
  }
}

function setHiddenCatalogAppIds(ids) {
  localStorage.setItem(HIDDEN_CATALOG_APPS_KEY, JSON.stringify(ids));
}

function mergeOrders(primaryOrders, fallbackOrders) {
  const seen = new Set();
  return [...primaryOrders, ...fallbackOrders]
    .filter((order) => {
      const key = order.order_id;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    })
    .sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
}

function readFileAsDataUrl(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

export default function Admin() {
  const { user } = useAuth();
  const [orders, setOrders] = useState([]);
  const [apps, setApps] = useState([]);
  const [hiddenCatalogIds, setHiddenCatalogIds] = useState([]);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [giftEmail, setGiftEmail] = useState("");
  const [giftAppId, setGiftAppId] = useState("");
  const [gifting, setGifting] = useState(false);

  const load = useCallback(async () => {
    const hiddenIds = getHiddenCatalogAppIds();
    setHiddenCatalogIds(hiddenIds);
    if (isFirebaseConfigured) {
      try {
        const [firebaseOrders, firebaseApps] = await Promise.all([
          listFirebaseOrders(),
          listFirebaseApps(),
        ]);
        setOrders(mergeOrders(firebaseOrders, getLocalOrders()));
        const visibleCatalogApps = FALLBACK_APPS.filter((a) => !hiddenIds.includes(a.app_id));
        // Merge catalog apps with Firebase apps (catalog apps take priority to avoid duplicates)
        const firebaseAppIds = new Set(firebaseApps.map((a) => a.app_id));
        const mergedApps = [...visibleCatalogApps, ...firebaseApps.filter((a) => !visibleCatalogApps.some((ca) => ca.app_id === a.app_id))];
        setApps(mergedApps);
      } catch {
        setOrders(getLocalOrders());
        setApps(FALLBACK_APPS.filter((a) => !hiddenIds.includes(a.app_id)));
      }
      return;
    }
    setOrders(getLocalOrders());
    setApps(FALLBACK_APPS.filter((a) => !hiddenIds.includes(a.app_id)));
  }, []);

  useEffect(() => { load(); }, [load]);

  const approve = async (id) => {
    const approveLocal = () => {
      const updatedOrders = getLocalOrders().map((order) =>
        order.order_id === id ? { ...order, status: "approved", reviewed_at: new Date().toISOString() } : order
      );
      setLocalOrders(updatedOrders);
      setOrders((current) => current.map((order) =>
        order.order_id === id ? { ...order, status: "approved", reviewed_at: new Date().toISOString() } : order
      ));
    };
    try {
      if (isFirebaseConfigured) {
        await updateFirebaseOrderStatus(id, "approved");
        approveLocal();
        toast.success("အတည်ပြုပြီးပါပြီ");
        load();
        return;
      }
      approveLocal(); toast.success("Local preview order အတည်ပြုပြီးပါပြီ");
    }
    catch (e) {
      approveLocal();
      toast.success("Local preview order အတည်ပြုပြီးပါပြီ");
    }
  };
  const reject = async (id) => {
    const rejectLocal = () => {
      const updatedOrders = getLocalOrders().map((order) =>
        order.order_id === id ? { ...order, status: "rejected", reviewed_at: new Date().toISOString() } : order
      );
      setLocalOrders(updatedOrders);
      setOrders((current) => current.map((order) =>
        order.order_id === id ? { ...order, status: "rejected", reviewed_at: new Date().toISOString() } : order
      ));
    };
    try {
      if (isFirebaseConfigured) {
        await updateFirebaseOrderStatus(id, "rejected");
        rejectLocal();
        toast.success("ငြင်းပယ်ပြီးပါပြီ");
        load();
        return;
      }
      rejectLocal(); toast.success("Local preview order ငြင်းပယ်ပြီးပါပြီ");
    }
    catch (e) {
      rejectLocal();
      toast.success("Local preview order ငြင်းပယ်ပြီးပါပြီ");
    }
  };
  const attachScreenshot = async (id, file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("ပုံဖိုင်သာ ထည့်ပါ");
      return;
    }
    const screenshotDataUrl = await readFileAsDataUrl(file);
    const updatedOrders = orders.map((order) =>
      order.order_id === id ? { ...order, screenshot_data_url: screenshotDataUrl } : order
    );
    setOrders(updatedOrders);
    setLocalOrders(updatedOrders);
    toast.success("Screenshot သိမ်းပြီးပါပြီ");
  };

  const confirmDelete = async () => {
    const id = deleteTarget?.id;
    if (!id) return;
    const isCatalogApp = FALLBACK_APPS.some((a) => a.app_id === id);

    try {
      // If it's a Firebase app (not a catalog app), delete from Firestore
      if (isFirebaseConfigured && !isCatalogApp) {
        await deleteFirebaseApp(id);
        setApps((current) => current.filter((app) => app.app_id !== id));
        toast.success("App ဖျက်ပြီးပါပြီ");
        setDeleteTarget(null);
        return;
      }

      // If it's a catalog app, hide it locally
      if (isCatalogApp) {
        const updatedHidden = [...new Set([...getHiddenCatalogAppIds(), id])];
        setHiddenCatalogAppIds(updatedHidden);
        setHiddenCatalogIds(updatedHidden);
        setApps((current) => current.filter((app) => app.app_id !== id));
        toast.success("App ကို စာရင်းမှ ဖယ်ရှားပြီးပါပြီ");
        setDeleteTarget(null);
        return;
      }

      // Fallback: just remove from local state
      setApps((current) => current.filter((app) => app.app_id !== id));
      toast.success("App ဖျက်ပြီးပါပြီ");
      setDeleteTarget(null);
    } catch (e) {
      toast.error(e?.message || "App ဖျက်ခြင်း မအောင်မြင်ပါ");
      setDeleteTarget(null);
    }
  };

  const restoreCatalogApp = (id) => {
    const updatedHidden = getHiddenCatalogAppIds().filter((hiddenId) => hiddenId !== id);
    setHiddenCatalogAppIds(updatedHidden);
    setHiddenCatalogIds(updatedHidden);
    const restoredApp = FALLBACK_APPS.find((a) => a.app_id === id);
    if (restoredApp) {
      setApps((current) => {
        if (current.some((a) => a.app_id === id)) return current;
        return [...current, restoredApp];
      });
    }
    toast.success("App ကို ပြန်ထည့်ပြီးပါပြီ");
  };

  const grantGift = async (event) => {
    event.preventDefault();
    const selectedApp = apps.find((app) => app.app_id === giftAppId);
    if (!giftEmail.trim() || !selectedApp) {
      toast.error("User email နှင့် App ကို ရွေးပါ");
      return;
    }
    if (!isFirebaseConfigured) {
      toast.error("Admin gifting requires Firebase to be configured");
      return;
    }
    setGifting(true);
    try {
      await grantFirebaseGift({ email: giftEmail, app: selectedApp, adminUser: user });
      toast.success(`${giftEmail.trim()} ကို ${selectedApp.title} လက်ဆောင်ပေးပြီးပါပြီ`);
      setGiftEmail("");
      setGiftAppId("");
      await load();
    } catch (error) {
      toast.error(error?.message || "Gift access ပေး၍ မရပါ");
    } finally {
      setGifting(false);
    }
  };

  const pending = orders.filter((o) => o.status === "pending");
  const reviewed = orders.filter((o) => o.status !== "pending");
  const hiddenApps = FALLBACK_APPS.filter((a) => hiddenCatalogIds.includes(a.app_id));

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <div className="text-sm tracking-wide text-zinc-500 font-medium">Admin Panel</div>
        <h1 className="text-3xl sm:text-4xl font-bold tracking-tight">ဈေးကွက်ထိန်းချုပ်မှု</h1>
        </div>
        <div className="flex gap-3">
          <div className="bg-white border border-zinc-200 rounded-xl px-5 py-3 text-center">
            <div className="text-[10px] tracking-wide text-zinc-500">စောင့်ဆိုင်း</div>
            <div className="font-bold text-2xl text-amber-600 tabular-nums">{pending.length}</div>
          </div>
          <div className="bg-white border border-zinc-200 rounded-xl px-5 py-3 text-center">
            <div className="text-[10px] tracking-wide text-zinc-500">App များ</div>
            <div className="font-bold text-2xl tabular-nums">{apps.length}</div>
          </div>
        </div>
      </div>

      <Tabs defaultValue="pending" className="mt-8">
        <TabsList className="bg-zinc-100 w-full justify-start overflow-x-auto">
          <TabsTrigger value="pending" data-testid="tab-pending">စောင့်ဆိုင်းနေသော အော်ဒါ ({pending.length})</TabsTrigger>
          <TabsTrigger value="reviewed" data-testid="tab-reviewed">စစ်ဆေးပြီး ({reviewed.length})</TabsTrigger>
          <TabsTrigger value="gifts" data-testid="tab-gifts" className="whitespace-nowrap">လက်ဆောင်ပေးရန်</TabsTrigger>
          <TabsTrigger value="apps" data-testid="tab-apps">App များ ({apps.length})</TabsTrigger>
        </TabsList>

        <TabsContent value="pending" className="mt-6 space-y-3">
          {pending.length === 0 && <div className="text-zinc-500 bg-white border border-zinc-200 rounded-2xl p-12 text-center">စောင့်ဆိုင်းနေသော အော်ဒါ မရှိပါ</div>}
          {pending.map((o) => (
            <OrderRow key={o.order_id} order={o} onApprove={approve} onReject={reject} onAttachScreenshot={attachScreenshot} />
          ))}
        </TabsContent>

        <TabsContent value="reviewed" className="mt-6 space-y-3">
          {reviewed.length === 0 && <div className="text-zinc-500 bg-white border border-zinc-200 rounded-2xl p-12 text-center">စစ်ဆေးပြီးသော အော်ဒါ မရှိပါ</div>}
          {reviewed.map((o) => (
            <OrderRow key={o.order_id} order={o} onAttachScreenshot={attachScreenshot} />
          ))}
        </TabsContent>

        <TabsContent value="gifts" className="mt-6">
          <form onSubmit={grantGift} className="max-w-2xl bg-white border border-zinc-200 rounded-2xl p-5 sm:p-7">
            <div className="flex items-start gap-3">
              <div className="rounded-xl bg-emerald-50 p-3 text-emerald-700">
                <Gift className="w-5 h-5" />
              </div>
              <div>
                <h2 className="font-semibold text-xl">User ကို Premium App လက်ဆောင်ပေးရန်</h2>
                <p className="text-sm text-zinc-500 mt-1">ဝယ်ယူခြင်း၊ payment screenshot နှင့် email confirmation မလိုဘဲ တိုက်ရိုက် approved access ပေးပါမည်။</p>
              </div>
            </div>
            <div className="grid gap-5 mt-6">
              <div>
                <Label htmlFor="gift-user-email">Registered user email</Label>
                <Input
                  id="gift-user-email"
                  data-testid="gift-user-email"
                  type="email"
                  required
                  value={giftEmail}
                  onChange={(event) => setGiftEmail(event.target.value)}
                  placeholder="user@example.com"
                  className="mt-1.5"
                />
              </div>
              <div>
                <Label htmlFor="gift-app">App</Label>
                <select
                  id="gift-app"
                  data-testid="gift-app"
                  required
                  value={giftAppId}
                  onChange={(event) => setGiftAppId(event.target.value)}
                  className="mt-1.5 flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                >
                  <option value="">App ရွေးပါ</option>
                  {apps.filter(isAppAvailable).map((app) => (
                    <option key={app.app_id} value={app.app_id}>{app.title}</option>
                  ))}
                </select>
              </div>
              <Button data-testid="grant-gift-btn" type="submit" disabled={gifting} className="w-full sm:w-fit bg-emerald-600 hover:bg-emerald-700 rounded-lg">
                <Gift className="w-4 h-4 mr-2" />
                {gifting ? "ပေးနေသည်…" : "Approved Gift ပေးမည်"}
              </Button>
            </div>
          </form>
        </TabsContent>

        <TabsContent value="apps" className="mt-6">
          <div className="flex justify-end mb-4">
            <Button asChild data-testid="add-app-btn" className="bg-[#7CC576] hover:bg-[#5FB259] rounded-lg">
              <Link to="/admin/apps/new"><Plus className="w-4 h-4 mr-2" /> App အသစ်ထည့်ရန်</Link>
            </Button>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {apps.length === 0 && (
              <div className="col-span-full text-zinc-500 bg-white border border-zinc-200 rounded-2xl p-12 text-center">
                App များ မရှိပါ။ အသစ်ထည့်ရန် "App အသစ်ထည့်ရန်" ကို နှိပ်ပါ။
              </div>
            )}
            {apps.map((a) => (
              <div key={a.app_id} className="bg-white border border-zinc-200 rounded-2xl p-5 flex items-center gap-4">
                <div className="w-14 h-14 rounded-xl bg-zinc-100 overflow-hidden border border-zinc-200">
                  {a.icon_url && <img src={fileUrl(a.icon_url)} alt="" className="w-full h-full object-cover" />}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-medium truncate flex items-center gap-2">
                    {a.title}
                    {!isAppAvailable(a) && (
                      <span className="inline-flex items-center rounded-md bg-zinc-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-zinc-500">
                        မကြာမီ
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-zinc-500">{a.category} · {a.price_mmk === 0 ? "အခမဲ့" : `${a.price_mmk.toLocaleString()} ကျပ်`}</div>
                </div>
                <Button data-testid={`delete-app-${a.app_id}`} variant="ghost" size="sm" onClick={() => setDeleteTarget({ id: a.app_id, title: a.title })} className="text-red-600 hover:text-red-700 hover:bg-red-50">
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            ))}
          </div>

          {/* Hidden catalog apps — restore section */}
          {hiddenApps.length > 0 && (
            <div className="mt-8 rounded-2xl border border-dashed border-zinc-300 bg-zinc-50 p-5">
              <div className="text-sm font-medium text-zinc-600 mb-3">ဖျက်ထားသော App များ (ပြန်ထည့်နိုင်ပါသည်)</div>
              <div className="flex flex-wrap gap-3">
                {hiddenApps.map((a) => (
                  <div key={a.app_id} className="flex items-center gap-3 bg-white border border-zinc-200 rounded-xl px-4 py-2.5">
                    <div className="w-9 h-9 rounded-lg bg-zinc-100 overflow-hidden border border-zinc-200">
                      {a.icon_url && <img src={fileUrl(a.icon_url)} alt="" className="w-full h-full object-cover" />}
                    </div>
                    <div className="text-sm font-medium text-zinc-700">{a.title}</div>
                    <Button
                      data-testid={`restore-app-${a.app_id}`}
                      variant="ghost"
                      size="sm"
                      onClick={() => restoreCatalogApp(a.app_id)}
                      className="text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50 h-8 px-2"
                    >
                      <RotateCcw className="w-3.5 h-3.5 mr-1" /> ပြန်ထည့်
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Delete confirmation dialog */}
      <Dialog open={Boolean(deleteTarget)} onOpenChange={(open) => !open && setDeleteTarget(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Confirm Delete</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-zinc-600">
            Are you sure you want to delete <span className="font-medium text-zinc-900">{deleteTarget?.title}</span>?
          </p>
          <p className="text-xs text-zinc-500">
            You can restore it later from the hidden apps section.
          </p>
          <div className="flex justify-end gap-2 mt-4">
            <Button variant="outline" size="sm" onClick={() => setDeleteTarget(null)}>
              Cancel
            </Button>
            <Button size="sm" onClick={confirmDelete} className="bg-red-600 hover:bg-red-700 text-white">
              <Trash2 className="w-4 h-4 mr-1" /> Delete
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

const STATUS_BADGE_CLASS = {
  approved: "border-emerald-200 text-emerald-700",
  rejected: "border-red-200 text-red-700",
  pending:  "border-amber-200 text-amber-700",
};

function OrderRow({ order, onApprove, onReject, onAttachScreenshot }) {
  const isPending = order.status === "pending";
  const badgeCls = STATUS_BADGE_CLASS[order.status] || STATUS_BADGE_CLASS.pending;
  const screenshotSrc = order.screenshot_data_url || fileUrl(order.screenshot_path);
  const firebaseScreenshotSrc = order.screenshot_url || screenshotSrc;
  const canReviewPayment = Boolean(firebaseScreenshotSrc);
  return (
    <div data-testid={`admin-order-${order.order_id}`} className="bg-white border border-zinc-200 rounded-2xl p-5 grid gap-4 lg:grid-cols-[minmax(0,1fr)_auto] lg:items-center">
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <div className="font-display font-semibold">{order.app_title}</div>
          <Badge variant="outline" className={badgeCls}>{order.status}</Badge>
        </div>
        <div className="text-xs text-zinc-500 mt-1 break-words">
          {order.user_name} · {order.user_email} · <span className="font-medium text-zinc-700 tabular-nums">{order.payment_method === "admin_gift" ? "Gift" : `${Number(order.amount_mmk || 0).toLocaleString()} Ks`}</span> via {getPaymentMethodLabel(order.payment_method)}
        </div>
        <div className="text-xs text-zinc-400 mt-0.5">{new Date(order.created_at).toLocaleString()}</div>
      </div>

      <Dialog>
        <DialogTrigger asChild>
          <Button data-testid={`view-screenshot-${order.order_id}`} variant={isPending ? "default" : "outline"} size="sm" className={isPending ? "bg-[#7CC576] hover:bg-[#5FB259] rounded-lg" : "border-zinc-300 rounded-lg"}>
            <Eye className="w-4 h-4 mr-1" /> {isPending ? "Payment စစ်ရန်" : "Screenshot ကြည့်ရန်"}
          </Button>
        </DialogTrigger>
        <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>ငွေပေးချေမှု စစ်ဆေးရန်</DialogTitle>
          </DialogHeader>
          <div className="grid gap-3 rounded-lg border border-zinc-200 bg-zinc-50 p-4 text-sm text-zinc-700 sm:grid-cols-2">
            <div>
              <div className="text-xs text-zinc-500">App</div>
              <div className="font-medium">{order.app_title}</div>
            </div>
            <div>
              <div className="text-xs text-zinc-500">Amount</div>
              <div className="font-medium tabular-nums">{order.payment_method === "admin_gift" ? "Admin Gift" : `${Number(order.amount_mmk || 0).toLocaleString()} Ks`}</div>
            </div>
            <div>
              <div className="text-xs text-zinc-500">Customer</div>
              <div className="font-medium break-words">{order.user_name} · {order.user_email}</div>
            </div>
            <div>
              <div className="text-xs text-zinc-500">Payment Method</div>
              <div className="font-medium">{getPaymentMethodLabel(order.payment_method)}</div>
            </div>
          </div>
          {firebaseScreenshotSrc ? (
            <div className="rounded-lg border border-zinc-200 bg-white p-3">
              <img src={firebaseScreenshotSrc} alt="Payment confirmation screenshot" className="max-h-[58vh] w-full rounded-md object-contain" />
            </div>
          ) : (
            <div className="rounded-lg border border-dashed border-zinc-300 bg-zinc-50 p-10 text-center text-sm text-zinc-500 space-y-4">
              <ImageOff className="mx-auto h-10 w-10 text-zinc-300" />
              <div>ဤအော်ဒါတွင် screenshot image မသိမ်းထားပါ။ Screenshot မရှိသေးပါက အတည်မပြုနိုင်ပါ။</div>
              <label className="inline-flex cursor-pointer items-center justify-center rounded-lg bg-[#7CC576] px-4 py-2 text-sm font-medium text-white hover:bg-[#5FB259]">
                Screenshot ထည့်ရန်
                <input
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={(event) => onAttachScreenshot?.(order.order_id, event.target.files?.[0])}
                />
              </label>
            </div>
          )}
          {firebaseScreenshotSrc && (
            <label className="mt-3 inline-flex cursor-pointer items-center justify-center rounded-lg border border-zinc-300 px-4 py-2 text-sm font-medium text-zinc-700 hover:bg-zinc-50">
              Screenshot ပြောင်းရန်
              <input
                type="file"
                accept="image/*"
                className="hidden"
                onChange={(event) => onAttachScreenshot?.(order.order_id, event.target.files?.[0])}
              />
            </label>
          )}
          {isPending && (
            <div className="flex flex-col-reverse gap-2 border-t border-zinc-200 pt-4 sm:flex-row sm:justify-end">
              <DialogClose asChild>
                <Button data-testid={`reject-${order.order_id}`} size="sm" variant="outline" onClick={() => onReject(order.order_id)} className="border-red-200 text-red-700 hover:bg-red-50">
                  <XCircle className="w-4 h-4 mr-1" /> ငြင်းပယ်
                </Button>
              </DialogClose>
              <DialogClose asChild>
                <Button data-testid={`approve-${order.order_id}`} size="sm" disabled={!canReviewPayment} onClick={() => onApprove(order.order_id)} className="bg-emerald-600 hover:bg-emerald-700 rounded-lg disabled:cursor-not-allowed disabled:bg-zinc-300">
                  <CheckCircle2 className="w-4 h-4 mr-1" /> Screenshot စစ်ပြီး အတည်ပြု
                </Button>
              </DialogClose>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
